<!-- TOPICS — narrow the pool, raise the floor, and the app's only settings.

     A pushed screen, so it takes the bar alone with a back button (SHELL.md).
     Every change saves immediately: there is no Save button, because there is
     nothing here that could fail and nothing worth confirming.

     THIS IS ALSO WHERE SETTINGS LIVE, and that is deliberate rather than lazy.
     The chassis shipped a SettingsModal that nothing in this app opened, so
     the vibration preference was unreachable - stored, and impossible to
     change - and so were the privacy, terms and support links. Rather than add
     a second entry point to the toolbar (SHELL.md allows one trailing action,
     and Topics is the better one), all of it lives together on the one screen
     that already means "how this game behaves". SettingsModal.vue is deleted;
     this is its replacement.

     There is NO SOUND TOGGLE. Nothing in this app plays audio, and a switch
     that changes nothing is worse than a missing one - it is the same defect
     the vibration toggle had. If sound ever ships, the control ships with it. -->
<template>
  <IonPage>
    <AppHeader title="Topics" back="/tabs/play" />

    <IonContent :fullscreen="true">
      <div class="pad" style="padding-top: var(--sp-4)">
        <p class="ex-eyebrow">What comes up</p>
        <p class="ex-sub" style="margin-top: 0">
          Tap to switch a topic off. Everything is on by default, which is the
          mix that keeps a mode feeling new.
        </p>
      </div>

      <div class="chip-wrap" style="margin-top: var(--sp-3)">
        <button
          v-for="t in TOPICS"
          :key="t.id"
          type="button"
          class="chip"
          :aria-pressed="String(on(t.id))"
          @click="toggle(t.id)"
        >
          <span aria-hidden="true">{{ t.emoji }}</span>
          {{ t.label }}
        </button>
      </div>

      <!-- One tap back to the full mix. Switching eleven chips back on by hand
           is the sort of chore that stops him ever narrowing them in the first
           place. -->
      <div v-if="someOff" class="pad" style="margin-top: var(--sp-3)">
        <IonButton fill="clear" size="small" @click="selectAll">
          Turn all {{ TOPICS.length }} back on
        </IonButton>
      </div>

      <p v-if="noneOn" class="pad ex-sub" style="color: var(--ex-hot)">
        Everything is switched off, so every mode will use the full mix instead.
        Switch at least one back on to actually narrow it.
      </p>

      <div class="pad" style="margin-top: var(--sp-6)">
        <p class="ex-eyebrow">How hard it starts</p>
        <p class="ex-sub" style="margin-top: 0">
          Warm-up and above are a floor, not a ceiling: harder questions still
          turn up, and Endurance climbs past them anyway.
          <strong>Stretching is different.</strong> It is the easy questions and
          nothing else, for a five- to eight-year-old — Endurance still speeds
          up on it, but it stops getting harder.
        </p>
      </div>

      <div class="pad" style="margin-top: var(--sp-3)">
        <IonSegment class="diff-segment" :value="difficulty" @ionChange="setDifficulty($event.detail.value)">
          <IonSegmentButton value="stretching"><IonLabel>Stretching</IonLabel></IonSegmentButton>
          <IonSegmentButton value="warm-up"><IonLabel>Warm-up</IonLabel></IonSegmentButton>
          <IonSegmentButton value="sweaty"><IonLabel>Sweaty</IonLabel></IonSegmentButton>
          <IonSegmentButton value="fully-austinated"><IonLabel>FULLY AUSTINATED</IonLabel></IonSegmentButton>
        </IonSegment>
      </div>

      <div class="pad" style="margin-top: var(--sp-6)">
        <p class="ex-eyebrow" style="margin:0">The clock</p>
        <p class="ex-sub" style="margin-top: var(--sp-2)">
          Switch it off and there is no rush at all — take as long as you like.
          Last Gasp and Endurance keep their clocks either way, because in those
          two the clock <em>is</em> the game.
        </p>
      </div>

      <IonList inset class="list-inset" style="margin-top: var(--sp-3)">
        <IonItem lines="none">
          <IonToggle
            :checked="timed"
            @ionChange="setTimed($event.detail.checked)"
          >
            Timer
          </IonToggle>
        </IonItem>
      </IonList>

      <div class="pad" style="margin-top: var(--sp-5)">
        <p class="ex-eyebrow" style="margin:0">Lives</p>
        <p class="ex-sub" style="margin-top: var(--sp-2)">
          Normally three lives and that is that. Switch this on and every right
          answer buys one back — up to three, so a bad patch is survivable but
          a bad run still ends.
        </p>
      </div>

      <IonList inset class="list-inset" style="margin-top: var(--sp-3)">
        <IonItem lines="none">
          <IonToggle
            :checked="regenLives"
            @ionChange="setRegen($event.detail.checked)"
          >
            Win a life back
          </IonToggle>
        </IonItem>
      </IonList>

      <!-- Only meaningful while there IS a clock, so it goes away with it
           rather than sitting there greyed out and inviting a tap. -->
      <template v-if="timed">
        <div class="pad" style="margin-top: var(--sp-5)">
          <p class="ex-eyebrow" style="margin:0">How much time</p>
          <p class="ex-sub" style="margin-top: var(--sp-2)">{{ scaleHint }}</p>
        </div>
        <div class="pad" style="margin-top: var(--sp-3)">
          <IonSegment class="diff-segment" :value="String(timeScale)" @ionChange="setScale($event.detail.value)">
            <IonSegmentButton v-for="t in TIME_SCALES" :key="t.value" :value="String(t.value)">
              <IonLabel>{{ t.label }}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </div>
      </template>

      <IonList inset class="list-inset" style="margin-top: var(--sp-6)">
        <IonListHeader>
          <IonLabel>
            <h3>How it feels</h3>
            <p>Buzzes on a right answer, a wrong one, and the clock.</p>
          </IonLabel>
        </IonListHeader>
        <IonItem lines="none">
          <IonToggle
            :checked="haptics"
            @ionChange="setHapticsPref($event.detail.checked)"
          >
            Vibration
          </IonToggle>
        </IonItem>
      </IonList>

      <!-- The chassis put these in SettingsModal, which nothing opened. They
           are the group every app needs and the App Store asks for, so they
           come here with the rest of the settings rather than staying
           unreachable. Full page loads, not router pushes: these are
           standalone pages outside the SPA that server.js serves. -->
      <IonList inset class="list-inset" style="margin-top: var(--sp-6)">
        <IonListHeader>
          <IonLabel>
            <h3>About</h3>
            <p>The boring but necessary bit.</p>
          </IonLabel>
        </IonListHeader>
        <IonItem button :detail="true" @click="goTo('/privacy')">
          <IonLabel><h3>Privacy policy</h3><p>What is stored, and where</p></IonLabel>
        </IonItem>
        <IonItem button :detail="true" @click="goTo('/terms')">
          <IonLabel><h3>Terms</h3><p>The rules of the thing</p></IonLabel>
        </IonItem>
        <IonItem button :detail="true" @click="goTo('/support')">
          <IonLabel><h3>Support</h3><p>Get in touch</p></IonLabel>
        </IonItem>
        <IonItem lines="none">
          <IonLabel><h3>Version</h3></IonLabel>
          <IonNote slot="end">{{ buildLabel }}</IonNote>
        </IonItem>
      </IonList>

      <div class="tail" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import {
  IonPage, IonContent, IonSegment, IonSegmentButton, IonLabel,
  IonList, IonListHeader, IonItem, IonToggle, IonButton, IonNote,
} from '@ionic/vue';
import { AppHeader, announce } from '../core/ui';
import { buildLabel } from '../core/pwa.js';
import { TOPICS, ALL_TOPIC_IDS, topicLabel } from '../game/topics.js';
import { profile, updateProfile, enabledTopics } from '../game/profile.js';
import { timed, timeScale, TIME_SCALES } from '../game/pace.js';
import { regenLives } from '../game/lives.js';
import { haptic } from '../core/util.js';

const selected = computed(() => enabledTopics.value);
const difficulty = computed(() => profile.value.difficulty || 'warm-up');
const haptics = computed(() => profile.value.haptics !== false);

const on = (id) => selected.value.includes(id);
const someOff = computed(() => selected.value.length < TOPICS.length);
const noneOn = computed(() => {
  try {
    const raw = JSON.parse(profile.value.topics ?? '[]');
    return Array.isArray(raw) && raw.length === 0;
  } catch {
    return false;
  }
});

function toggle(id) {
  haptic('selection');
  // enabledTopics reports ALL when the stored value is empty or unreadable, so
  // switching one off from that state has to start from the full list rather
  // than from an empty array — otherwise the first tap switches everything ON.
  const base = selected.value.length ? selected.value : ALL_TOPIC_IDS;
  const next = base.includes(id) ? base.filter((t) => t !== id) : [...base, id];
  updateProfile({ topics: JSON.stringify(next) });
  // A chip's pressed state is a fill, which a screen reader does not see.
  announce(`${topicLabel(id)} ${next.includes(id) ? 'on' : 'off'}`);
}

function selectAll() {
  haptic('pop');
  updateProfile({ topics: JSON.stringify(ALL_TOPIC_IDS) });
  announce('All topics on');
}

function setDifficulty(value) {
  if (!value || value === difficulty.value) return;
  haptic('selection');
  updateProfile({ difficulty: value });
}

function setHapticsPref(onNow) {
  if (onNow === haptics.value) return;
  updateProfile({ haptics: !!onNow });
  // Buzz on the way ON only — buzzing to confirm you have switched buzzing off
  // is a joke the app should not be making. App.vue's watcher has already
  // applied the preference by the time this runs.
  if (onNow) haptic('success');
}

// A full page load, not a router push: /privacy, /terms and /support are
// server-rendered pages outside the SPA.
const goTo = (path) => { location.href = path; };

const scaleHint = computed(
  () => TIME_SCALES.find((t) => t.value === timeScale.value)?.hint ?? '',
);

function setTimed(on) {
  if (on === timed.value) return;
  updateProfile({ timed: !!on });
  haptic('selection');
  announce(on ? 'Timer on' : 'Timer off. No time pressure.');
}

function setScale(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === timeScale.value) return;
  updateProfile({ timeScale: n });
  haptic('selection');
}

function setRegen(on) {
  if (on === regenLives.value) return;
  updateProfile({ regenLives: !!on });
  haptic('selection');
  announce(on ? 'Right answers win a life back' : 'Three lives, no coming back');
}
</script>
